/**
 * AP-ACCEPT-01C: Internal Acceptance Review Page
 * Route: /finance/acceptance-queue/:submissionId (protected)
 *
 * Full review with PO context, evidence preview, and accept/partial/reject/clarify actions.
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, AlertTriangle, XCircle, MessageCircle, ChevronDown, ChevronUp, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const REJECTION_REASONS = [
  { value: "SCOPE_NOT_MET",           label: "Scope Not Met" },
  { value: "QUANTITY_SHORT",          label: "Quantity Short" },
  { value: "QUALITY_DEFICIENT",       label: "Quality Deficient" },
  { value: "WRONG_ITEM",              label: "Wrong Item" },
  { value: "DUPLICATE_SUBMISSION",    label: "Duplicate Submission" },
  { value: "DOCUMENTATION_INCOMPLETE",label: "Documentation Incomplete" },
  { value: "OTHER",                   label: "Other" },
];

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

const STATUS_LABELS = {
  PENDING_REVIEW: { label: "Pending Review", color: "#92400E", bg: "#FEF3C7" },
  UNDER_REVIEW: { label: "Under Review", color: "#1E40AF", bg: "#EFF6FF" },
  ACCEPTED: { label: "Accepted", color: "#166534", bg: "#F0FDF4" },
  PARTIALLY_ACCEPTED: { label: "Partially Accepted", color: "#166534", bg: "#F0FDF4" },
  REJECTED: { label: "Rejected", color: "#991B1B", bg: "#FEF2F2" },
  CORRECTION_REQUESTED: { label: "Correction Requested", color: "#9A3412", bg: "#FFF7ED" },
  RESUBMITTED: { label: "Resubmitted", color: "#5B21B6", bg: "#F5F3FF" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_LABELS[status] || { label: status, color: "#666", bg: "#F5F5F5" };
  return <span style={{ padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
}

export default function AcceptanceReview() {
  const { submissionId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionPanel, setActionPanel] = useState(null); // "accept" | "partial" | "reject" | "clarify"

  // Action states
  const [acceptNotes, setAcceptNotes] = useState("");
  const [partialDecisions, setPartialDecisions] = useState([]);
  const [partialNotes, setPartialNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("SCOPE_NOT_MET");
  const [rejectNotes, setRejectNotes] = useState("");
  const [clarifyNotes, setClarifyNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [linesExpanded, setLinesExpanded] = useState(true);

  useEffect(() => { load(); }, [submissionId]);

  async function load() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/ap/acceptance/${submissionId}`, { withCredentials: true });
      setDetail(res.data);
      setPartialDecisions((res.data.line_submissions || []).map((l) => ({
        po_line_id: l.po_line_id,
        description: l.po_line_description,
        delivered_quantity: l.delivered_quantity,
        accepted_quantity: l.delivered_quantity,
        rejected_quantity: "0",
        reason_code: null,
        line_notes: "",
      })));
    } catch (err) {
      setError(err.response?.status === 403 ? "Access denied" : "Failed to load submission");
    } finally {
      setLoading(false);
    }
  }

  async function startReview() {
    try {
      await axios.post(`${API}/api/ap/acceptance/${submissionId}/start-review`, {}, { withCredentials: true });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start review");
    }
  }

  async function handleFullAccept() {
    setSubmitting(true); setActionError("");
    try {
      await axios.post(`${API}/api/ap/acceptance/${submissionId}/accept`, { notes: acceptNotes.trim() || undefined }, { withCredentials: true });
      toast.success("Delivery fully accepted");
      await load(); setActionPanel(null);
    } catch (err) {
      setActionError(err.response?.data?.detail || "Acceptance failed");
    } finally { setSubmitting(false); }
  }

  async function handlePartialAccept() {
    setSubmitting(true); setActionError("");
    for (const d of partialDecisions) {
      const acc = parseFloat(d.accepted_quantity || "0");
      const rej = parseFloat(d.rejected_quantity || "0");
      const del = parseFloat(d.delivered_quantity);
      if (Math.abs(acc + rej - del) > 0.001) {
        setActionError(`Accepted + Rejected must equal Delivered for "${d.description.slice(0, 40)}"`);
        setSubmitting(false); return;
      }
    }
    try {
      await axios.post(`${API}/api/ap/acceptance/${submissionId}/partial-accept`, {
        notes: partialNotes,
        line_decisions: partialDecisions.map((d) => ({
          po_line_id: d.po_line_id,
          accepted_quantity: String(parseFloat(d.accepted_quantity || "0")),
          rejected_quantity: String(parseFloat(d.rejected_quantity || "0")),
          reason_code: d.reason_code,
          line_notes: d.line_notes,
        })),
      }, { withCredentials: true });
      toast.success("Partial acceptance recorded");
      await load(); setActionPanel(null);
    } catch (err) {
      setActionError(err.response?.data?.detail || "Partial acceptance failed");
    } finally { setSubmitting(false); }
  }

  async function handleReject() {
    if (!rejectNotes.trim()) { setActionError("Rejection notes are required"); return; }
    setSubmitting(true); setActionError("");
    try {
      await axios.post(`${API}/api/ap/acceptance/${submissionId}/reject`, { reason_code: rejectReason, notes: rejectNotes }, { withCredentials: true });
      toast.success("Submission rejected");
      await load(); setActionPanel(null);
    } catch (err) {
      setActionError(err.response?.data?.detail || "Rejection failed");
    } finally { setSubmitting(false); }
  }

  async function handleClarify() {
    if (!clarifyNotes.trim()) { setActionError("Please describe what correction is needed"); return; }
    setSubmitting(true); setActionError("");
    try {
      await axios.post(`${API}/api/ap/acceptance/${submissionId}/request-clarification`, { notes: clarifyNotes }, { withCredentials: true });
      toast.success("Clarification requested from supplier");
      await load(); setActionPanel(null);
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to request clarification");
    } finally { setSubmitting(false); }
  }

  if (loading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ width: "28px", height: "28px", border: "2px solid #EDE5D8", borderTopColor: "#C9A15F", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;
  if (error || !detail) return <div style={{ padding: "60px 24px", textAlign: "center" }}><p style={{ color: "#DC2626" }}>{error || "Submission not found"}</p><button onClick={() => navigate("/ap/acceptance")} style={btnSecondary}>Back to Queue</button></div>;

  const sub = detail;
  const isDecided = ["ACCEPTED", "PARTIALLY_ACCEPTED", "REJECTED"].includes(sub.status);
  const canAct = ["PENDING_REVIEW", "UNDER_REVIEW", "RESUBMITTED"].includes(sub.status);

  return (
    <div data-testid="acceptance-review-page" style={{ minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "32px 24px" }}>
        <button onClick={() => navigate("/ap/acceptance")} style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#8A7A6A", background: "none", border: "none", cursor: "pointer", padding: "0 0 20px" }}>
          <ArrowLeft size={14} /> Back to Queue
        </button>

        {/* Header */}
        <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", padding: "24px 28px", marginBottom: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
            <div>
              <h1 data-testid="review-po-number" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "22px", fontWeight: 600, color: "#1A1A1A", margin: "0 0 4px" }}>{sub.po_number || "Purchase Order"}</h1>
              <p style={{ fontSize: "12px", color: "#9A8A78", margin: 0 }}>Submitted by {sub.submitted_by_email} · {fmtDate(sub.submitted_at)}</p>
              {sub.resubmission_count > 0 && <p style={{ fontSize: "11px", color: "#5B21B6", margin: "2px 0 0" }}>Resubmission #{sub.resubmission_count}</p>}
            </div>
            <StatusBadge status={sub.status} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "14px" }}>
            {[
              { label: "Supplier", value: sub.supplier_name || sub.supplier_id?.slice(0, 12) },
              { label: "Event", value: sub.event_name || "—" },
              { label: "Lines Submitted", value: sub.line_submissions?.length },
              { label: "Reviewed by", value: sub.reviewed_by_email || "—" },
            ].map((m) => (
              <div key={m.label}>
                <p style={{ fontSize: "10px", color: "#B0A090", margin: "0 0 2px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{m.label}</p>
                <p style={{ fontSize: "13px", color: "#1A1A1A", margin: 0, fontWeight: 500 }}>{m.value || "—"}</p>
              </div>
            ))}
          </div>

          {sub.review_notes && (
            <div style={{ marginTop: "12px", padding: "10px 14px", background: "#F9F5EE", borderRadius: "8px", fontSize: "13px", color: "#4A3A2A" }}>
              <span style={{ fontSize: "10px", color: "#9A8A78", textTransform: "uppercase", letterSpacing: "0.5px" }}>Last review note: </span>
              {sub.review_notes}
            </div>
          )}
        </div>

        {/* Start review banner */}
        {sub.status === "PENDING_REVIEW" && (
          <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "10px", padding: "14px 20px", marginBottom: "16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <p style={{ fontSize: "13px", color: "#92400E", margin: 0 }}>This submission is waiting for review. Claim it to begin.</p>
            <button data-testid="start-review-btn" onClick={startReview} style={{ padding: "8px 16px", background: "#1A1A1A", color: "#F5EFE6", border: "none", borderRadius: "8px", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}>Start Review</button>
          </div>
        )}

        {/* PO Context */}
        {sub.po && (
          <div style={{ background: "#1A1A1A", borderRadius: "12px", padding: "18px 24px", marginBottom: "16px", display: "flex", gap: "28px", flexWrap: "wrap" }}>
            <div>
              <p style={{ fontSize: "10px", color: "#6B5B4A", margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.5px" }}>PO Total</p>
              <p style={{ fontSize: "18px", fontWeight: 700, color: "#C9A15F", margin: 0 }}>{fmt(sub.po.total_minor, sub.po.currency_code)}</p>
            </div>
            <div>
              <p style={{ fontSize: "10px", color: "#6B5B4A", margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.5px" }}>PO Status</p>
              <p style={{ fontSize: "14px", color: "#F5EFE6", margin: 0 }}>{sub.po.status}</p>
            </div>
            <div>
              <p style={{ fontSize: "10px", color: "#6B5B4A", margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Delivery Period</p>
              <p style={{ fontSize: "13px", color: "#A89880", margin: 0 }}>{fmtDate(sub.po.delivery_start_date)} – {fmtDate(sub.po.delivery_end_date)}</p>
            </div>
          </div>
        )}

        {/* Submitted lines */}
        <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", overflow: "hidden", marginBottom: "16px" }}>
          <button onClick={() => setLinesExpanded(!linesExpanded)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", background: "#FAF6F0", border: "none", cursor: "pointer", borderBottom: linesExpanded ? "1px solid #EDE5D8" : "none" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A" }}>Submitted Lines ({sub.line_submissions?.length || 0})</span>
            {linesExpanded ? <ChevronUp size={15} style={{ color: "#9A8A78" }} /> : <ChevronDown size={15} style={{ color: "#9A8A78" }} />}
          </button>
          {linesExpanded && (sub.line_submissions || []).map((l, idx) => (
            <div key={idx} data-testid={`review-line-${idx}`} style={{ padding: "16px 24px", borderBottom: idx < sub.line_submissions.length - 1 ? "1px solid #F0EBE0" : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>{l.po_line_description}</p>
                <span style={{ fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "8px", background: "#EDE5D8", color: "#6B5B4A" }}>{l.line_review_status || "PENDING"}</span>
              </div>
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "12px", color: "#6B5B4A" }}>
                <span>PO Qty: <strong>{l.po_line_quantity} {l.po_line_unit}</strong></span>
                <span style={{ color: "#1A1A1A", fontWeight: 600 }}>Delivered: {l.delivered_quantity}</span>
                <span>Ref: {l.delivery_reference}</span>
                <span>Date: {fmtDate(l.delivery_date)}</span>
                {l.milestone && <span>Milestone: {l.milestone}</span>}
              </div>
              {l.evidence_documents?.length > 0 && (
                <div style={{ marginTop: "6px" }}>
                  <span style={{ fontSize: "10px", color: "#9A8A78", textTransform: "uppercase", letterSpacing: "0.5px" }}>Evidence: </span>
                  {l.evidence_documents.map((d, i) => (
                    <span key={i} style={{ fontSize: "11px", background: "#F0EBE0", padding: "2px 6px", borderRadius: "4px", marginRight: "4px", color: "#6B5B4A" }}>{d.document_name} ({d.document_type})</span>
                  ))}
                </div>
              )}
              {l.notes && <p style={{ fontSize: "12px", color: "#8A7A6A", marginTop: "4px", fontStyle: "italic" }}>{l.notes}</p>}
            </div>
          ))}
        </div>

        {/* Action Buttons (only when can act) */}
        {canAct && (
          <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", padding: "20px 24px", marginBottom: "16px" }}>
            <p style={{ fontSize: "11px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase", margin: "0 0 14px" }}>Review Actions</p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button data-testid="full-accept-btn" onClick={() => { setActionPanel(actionPanel === "accept" ? null : "accept"); setActionError(""); }} style={{ ...actionBtn, background: "#166534", color: "#fff", border: "none" }}><CheckCircle2 size={14} /> Full Accept</button>
              <button data-testid="partial-accept-btn" onClick={() => { setActionPanel(actionPanel === "partial" ? null : "partial"); setActionError(""); }} style={{ ...actionBtn, background: "#1D4ED8", color: "#fff", border: "none" }}><FileText size={14} /> Partial Accept</button>
              <button data-testid="reject-btn" onClick={() => { setActionPanel(actionPanel === "reject" ? null : "reject"); setActionError(""); }} style={{ ...actionBtn, background: "#DC2626", color: "#fff", border: "none" }}><XCircle size={14} /> Reject</button>
              <button data-testid="clarify-btn" onClick={() => { setActionPanel(actionPanel === "clarify" ? null : "clarify"); setActionError(""); }} style={{ ...actionBtn, background: "transparent", color: "#9A3412", border: "1px solid #FED7AA" }}><MessageCircle size={14} /> Request Clarification</button>
            </div>

            {actionError && <p style={{ fontSize: "12px", color: "#DC2626", marginTop: "10px" }}>{actionError}</p>}

            {/* Accept panel */}
            {actionPanel === "accept" && (
              <div data-testid="accept-panel" style={panelStyle}>
                <p style={panelTitle}>Full Acceptance</p>
                <p style={panelHint}>All delivered quantities will be accepted as submitted.</p>
                <textarea value={acceptNotes} onChange={(e) => setAcceptNotes(e.target.value)} placeholder="Optional acceptance notes…" rows={2} style={{ ...tArea, marginBottom: "10px" }} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button data-testid="confirm-accept-btn" onClick={handleFullAccept} disabled={submitting} style={confirmBtn}>{submitting ? "Processing…" : "Confirm Full Acceptance"}</button>
                  <button onClick={() => setActionPanel(null)} style={cancelBtn}>Cancel</button>
                </div>
              </div>
            )}

            {/* Partial panel */}
            {actionPanel === "partial" && (
              <div data-testid="partial-panel" style={panelStyle}>
                <p style={panelTitle}>Partial Acceptance</p>
                <p style={panelHint}>Set accepted and rejected quantities per line. Accepted + Rejected must equal Delivered.</p>
                {partialDecisions.map((d, idx) => (
                  <div key={d.po_line_id} style={{ background: "#F9F5EE", borderRadius: "8px", padding: "12px", marginBottom: "10px" }}>
                    <p style={{ fontSize: "12px", fontWeight: 600, color: "#1A1A1A", margin: "0 0 8px" }}>{d.description} — Delivered: {d.delivered_quantity}</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                      <div>
                        <label style={microLabel}>Accepted qty</label>
                        <input type="number" min="0" step="0.001" value={d.accepted_quantity}
                          onChange={(e) => {
                            const acc = parseFloat(e.target.value || "0");
                            const rej = Math.max(0, parseFloat(d.delivered_quantity) - acc);
                            setPartialDecisions((prev) => prev.map((x, i) => i === idx ? { ...x, accepted_quantity: e.target.value, rejected_quantity: String(Math.round(rej * 1000) / 1000) } : x));
                          }}
                          style={smallInput}
                        />
                      </div>
                      <div>
                        <label style={microLabel}>Rejected qty</label>
                        <input type="number" min="0" step="0.001" value={d.rejected_quantity}
                          onChange={(e) => setPartialDecisions((prev) => prev.map((x, i) => i === idx ? { ...x, rejected_quantity: e.target.value } : x))}
                          style={smallInput}
                        />
                      </div>
                      <div>
                        <label style={microLabel}>Line notes</label>
                        <input type="text" value={d.line_notes} onChange={(e) => setPartialDecisions((prev) => prev.map((x, i) => i === idx ? { ...x, line_notes: e.target.value } : x))} placeholder="Optional" style={smallInput} />
                      </div>
                    </div>
                  </div>
                ))}
                <textarea value={partialNotes} onChange={(e) => setPartialNotes(e.target.value)} placeholder="Overall notes (required for partial acceptance)…" rows={2} required style={{ ...tArea, marginBottom: "10px" }} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button data-testid="confirm-partial-btn" onClick={handlePartialAccept} disabled={submitting || !partialNotes.trim()} style={{ ...confirmBtn, background: "#1D4ED8" }}>{submitting ? "Processing…" : "Confirm Partial Acceptance"}</button>
                  <button onClick={() => setActionPanel(null)} style={cancelBtn}>Cancel</button>
                </div>
              </div>
            )}

            {/* Reject panel */}
            {actionPanel === "reject" && (
              <div data-testid="reject-panel" style={panelStyle}>
                <p style={panelTitle}>Rejection</p>
                <select value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} style={{ ...smallInput, marginBottom: "8px", width: "auto" }}>
                  {REJECTION_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} placeholder="Rejection reason (required)…" rows={3} required style={{ ...tArea, marginBottom: "10px" }} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button data-testid="confirm-reject-btn" onClick={handleReject} disabled={submitting || !rejectNotes.trim()} style={{ ...confirmBtn, background: "#DC2626" }}>{submitting ? "Processing…" : "Confirm Rejection"}</button>
                  <button onClick={() => setActionPanel(null)} style={cancelBtn}>Cancel</button>
                </div>
              </div>
            )}

            {/* Clarify panel */}
            {actionPanel === "clarify" && (
              <div data-testid="clarify-panel" style={panelStyle}>
                <p style={panelTitle}>Request Clarification</p>
                <p style={panelHint}>The supplier will be able to resubmit corrected evidence.</p>
                <textarea value={clarifyNotes} onChange={(e) => setClarifyNotes(e.target.value)} placeholder="Describe what correction is required…" rows={3} required style={{ ...tArea, marginBottom: "10px" }} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button data-testid="confirm-clarify-btn" onClick={handleClarify} disabled={submitting || !clarifyNotes.trim()} style={{ ...confirmBtn, background: "#9A3412" }}>{submitting ? "Sending…" : "Send Clarification Request"}</button>
                  <button onClick={() => setActionPanel(null)} style={cancelBtn}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Acceptance records history */}
        {(detail.acceptance_records || []).length > 0 && (
          <div data-testid="acceptance-history" style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", padding: "20px 24px" }}>
            <p style={{ fontSize: "11px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase", margin: "0 0 14px" }}>Acceptance Records ({detail.acceptance_records.length})</p>
            {detail.acceptance_records.map((r, idx) => (
              <div key={r.id} data-testid={`acceptance-record-${idx}`} style={{ padding: "12px", background: "#FAF6F0", borderRadius: "8px", marginBottom: "8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginBottom: "6px" }}>
                  <p style={{ fontSize: "12px", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>{r.po_line_description}</p>
                  <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: "6px", background: r.acceptance_status === "ACCEPTED" ? "#F0FDF4" : r.acceptance_status === "REJECTED" ? "#FEF2F2" : "#EFF6FF", color: r.acceptance_status === "ACCEPTED" ? "#166534" : r.acceptance_status === "REJECTED" ? "#991B1B" : "#1E40AF" }}>{r.record_type !== "ORIGINAL" ? `[${r.record_type}] ` : ""}{r.acceptance_status}</span>
                </div>
                <div style={{ display: "flex", gap: "16px", fontSize: "11px", color: "#6B5B4A" }}>
                  <span>Accepted: <strong>{r.accepted_quantity}</strong></span>
                  <span>Rejected: <strong>{r.rejected_quantity}</strong></span>
                  <span>By: {r.reviewed_by_email}</span>
                  <span>{fmtDate(r.reviewed_at)}</span>
                  {!r.is_active && <span style={{ color: "#DC2626" }}>REVERSED</span>}
                </div>
                {r.review_notes && <p style={{ fontSize: "11px", color: "#8A7A6A", marginTop: "4px", fontStyle: "italic" }}>{r.review_notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const actionBtn = { display: "inline-flex", alignItems: "center", gap: "6px", padding: "9px 16px", borderRadius: "8px", fontSize: "12px", fontWeight: 600, cursor: "pointer", transition: "opacity 0.2s" };
const btnSecondary = { padding: "8px 16px", background: "#1A1A1A", color: "#F5EFE6", border: "none", borderRadius: "8px", fontSize: "12px", cursor: "pointer" };
const panelStyle = { marginTop: "14px", padding: "16px", background: "#F9F5EE", borderRadius: "10px", border: "1px solid #EDE5D8" };
const panelTitle = { fontSize: "13px", fontWeight: 600, color: "#1A1A1A", margin: "0 0 6px" };
const panelHint = { fontSize: "12px", color: "#8A7A6A", margin: "0 0 12px" };
const confirmBtn = { padding: "8px 18px", background: "#166534", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: 600, cursor: "pointer" };
const cancelBtn = { padding: "8px 14px", background: "transparent", border: "1px solid #D4C5B0", borderRadius: "6px", fontSize: "13px", color: "#8A7A6A", cursor: "pointer" };
const tArea = { width: "100%", padding: "9px 12px", border: "1px solid #D4C5B0", borderRadius: "8px", fontSize: "13px", color: "#1A1A1A", background: "#FFFFFF", outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: "'DM Sans', sans-serif" };
const smallInput = { padding: "7px 10px", border: "1px solid #D4C5B0", borderRadius: "6px", fontSize: "12px", color: "#1A1A1A", background: "#FFFFFF", outline: "none", width: "100%", boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif" };
const microLabel = { display: "block", fontSize: "10px", color: "#9A8A78", marginBottom: "3px", textTransform: "uppercase", letterSpacing: "0.5px" };
